'use client';

import { useEffect, useState } from 'react';
import { NestedHome, SpLoanCalculatorConfig } from '@habity/types';
import { ROUTES } from '@habity/constants';
import { useAgency } from '../useAgency';
import { hasProvider, getProviderSettings } from '@habity/agencies';
import { isSold } from '@habity/services';

// Global state to track script loading
let scriptLoaded = false;
let scriptLoading = false;
const scriptPromises: Promise<void>[] = [];

// Function to load script and wait for it to be ready
const ensureScriptLoaded = (): Promise<void> => {
  if (scriptLoaded) {
    return Promise.resolve();
  }

  if (scriptLoading) {
    return scriptPromises[scriptPromises.length - 1];
  }

  scriptLoading = true;
  const isDev = false;
  const url = isDev
    ? 'https://bolan-test.realalliance.se/bundle.js'
    : 'https://bolan.realalliance.se/bundle.js';

  const promise = new Promise<void>((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.getElementById('loan-calculator-script');
    if (existingScript) {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.id = 'loan-calculator-script';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('Loan calculator script loaded successfully');
      scriptLoaded = true;
      scriptLoading = false;

      // Wait a bit for the custom element to be defined
      const checkCustomElement = () => {
        if (customElements.get('sp-bolanekalkylator')) {
          console.log('Custom element sp-bolanekalkylator is defined');
          resolve();
        } else {
          setTimeout(checkCustomElement, 50);
        }
      };

      setTimeout(checkCustomElement, 100);
    };

    script.onerror = (error) => {
      console.error('Failed to load loan calculator script', error);
      scriptLoading = false;
      reject(error);
    };

    document.head.appendChild(script);
  });

  scriptPromises.push(promise);
  return promise;
};

export const useLoanCalculator = (home: NestedHome | null) => {
  const [loadFailed, setLoadFailed] = useState(false);
  const [calculatorReady, setCalculatorReady] = useState(false);
  const { agency } = useAgency();
  const { price, status } = home || {};

  // Check if loan calculator should be displayed at all
  const shouldDisplayLoanCalculator =
    price && home && !isSold(home) && status?.show_address;
  // Check if loan calculator provider is enabled
  const isLoanCalculatorEnabled =
    hasProvider(agency, 'sp_loan_calculator') && shouldDisplayLoanCalculator;
  const loanCalculatorConfig = isLoanCalculatorEnabled
    ? (getProviderSettings(
        agency,
        'sp_loan_calculator'
      ) as SpLoanCalculatorConfig)
    : null;

  useEffect(() => {
    if (!home || !isLoanCalculatorEnabled) {
      return;
    }

    const initializeCalculator = async () => {
      try {
        // Ensure script is loaded first
        await ensureScriptLoaded();

        // Wait a bit more to ensure everything is ready
        await new Promise((resolve) => setTimeout(resolve, 200));

        setCalculatorReady(true);
        setLoadFailed(false);
      } catch (error) {
        console.error('Failed to initialize calculator:', error);
        setLoadFailed(true);
        setCalculatorReady(false);
      }
    };

    initializeCalculator();

    return () => {
      setCalculatorReady(false);
    };
  }, [home, isLoanCalculatorEnabled]);

  // Calculate values for the calculator
  const streetAddress = home?.address?.street || '';
  const expectedPrice = home?.price?.toString() || '0';

  // Get operating cost from provider_data.operation.sum (Driftskostnader->Totalt)
  // Divide by 12 to get monthly value since the data is annual
  const operatingCostFromData =
    (home?.provider_data as any)?.operation?.sum ||
    (home?.provider_data as any)?.mainResidentialBuilding?.operation?.sum;
  const operatingCost = operatingCostFromData
    ? Math.round(operatingCostFromData / 12).toString()
    : '0';

  const monthlyCost = (home?.address?.monthly_fee || 0).toString();
  const privacyPolicyUrl = ROUTES.CMS.PRIVACY;

  // Get organization details from provider config
  const organization = loanCalculatorConfig?.organization || 'eo-demo';
  const organizationUnit =
    loanCalculatorConfig?.organizationUnit || 'eo-demo-unit';

  // Return JSX that renders the calculator using dangerouslySetInnerHTML (like test.html)
  return {
    calculatorElement:
      calculatorReady && isLoanCalculatorEnabled ? (
        <div
          className="mt-6"
          dangerouslySetInnerHTML={{
            __html: `<sp-bolanekalkylator
            street-address="${streetAddress}"
            expected-final-price="${expectedPrice}"
            operating-cost="${operatingCost}"
            monthly-cost="${monthlyCost}"
            organization="${organization}"
            organization-unit="${organizationUnit}"
            privacy-policy-url="${privacyPolicyUrl}"
            style="--primary-color: rgb(20,9,0); --primary-color-foreground: #fff; --background-color: #fffcf5; --font-family: 'Inter', sans-serif;"
          ></sp-bolanekalkylator>`,
          }}
        />
      ) : null,
    loadFailed,
  };
};
